/**
 * Rendering Engine — the rendering contract (data).
 *
 * Every constant the engine resolves against lives here, so behaviour is
 * data-driven and tunable without touching logic. Nothing in this module knows
 * anything about AI, research, thinking, memory, the library, the art director,
 * or the revision loop. It only renders.
 */

import { LAYER_BANDS } from '../schema';

export const RENDERER_ID = 'renderer/1';
export const RENDERER_VERSION = 1;

/** Stacking order: earlier bands sit behind later ones. */
export const BAND_ORDER = Object.freeze([...LAYER_BANDS]);

/** The type scale: fontSize = round(base * ratio ** step). Authored in px. */
export const TYPE_SCALE = Object.freeze({ base: 16, ratio: 1.25, min: 8, max: 400 });

/**
 * Canvas units (Design JSON V2 defines exactly `px` and `mm`).
 *
 * Tokens — space, radius, the type scale, intrinsic sizes — are authored in px.
 * A canvas measured in mm must therefore convert them, or a 24px pad becomes
 * 24mm and the geometry collapses. This is the px → canvas-unit factor.
 */
export const UNIT_SCALE = Object.freeze({
  px: 1,
  mm: 25.4 / 96, // CSS reference pixel: 96px per inch
});

/** Node kinds that may own children. Every other node is a leaf. */
export const CONTAINER_KINDS = Object.freeze(['group', 'frame']);

/** Fallback space steps when a document does not define its own. */
export const SPACE_FALLBACK = Object.freeze({ xs: 4, sm: 8, md: 16, lg: 24, xl: 40, '2xl': 64, '3xl': 96 });

/** Fallback radius steps. */
export const RADIUS_FALLBACK = Object.freeze({ none: 0, sm: 4, md: 12, lg: 24, pill: 999 });

/** Fallback colours, used only when a document leaves a role unset. */
export const COLOR_FALLBACK = Object.freeze({
  primary: '#111111', secondary: '#444444', accent: '#c9a227', surface: '#ffffff',
  onSurface: '#111111', muted: '#7a7a7a', success: '#1f9d55', warning: '#d97706',
});

/** Intrinsic sizing when a node declares no size and nothing else constrains it. */
export const INTRINSIC = Object.freeze({
  iconSize: 48,
  lineThickness: 2,
  imageRatio: 0.66,      // height = width * ratio
  shapeHeight: 120,
  charWidthFactor: 0.52, // average glyph advance as a fraction of font size
  minTextHeight: 12,
});

/**
 * Auto-layout defaults — an exact mirror of the frozen `createAutoLayout`
 * contract. Phase 1 is the single source of truth for this vocabulary:
 * `{ type, gap, pad, align, distribute, sizing, grid }`.
 */
export const LAYOUT_DEFAULTS = Object.freeze({
  type: 'stack',
  gap: 'md',
  pad: 0,
  align: 'start',
  distribute: 'start',
  sizing: 'hug',
  grid: null,
});

/** Columns a grid layout falls back to when its grid spec names none. */
export const DEFAULT_GRID_COLS = 12;

/** Sections stack down the page with this gap unless the page says otherwise. */
export const SECTION_GAP = 'lg';

/** Design JSON V2 shape kinds → the editor's shape ids. */
export const SHAPE_MAP = Object.freeze({
  rect: 'rect',
  'rounded-rect': 'roundedRect',
  square: 'rect',
  circle: 'circle',
  ellipse: 'circle',
  triangle: 'triangle',
  polygon: 'polygon',
  star: 'star',
  arrow: 'arrow',
  line: 'rect',
  blob: 'circle',
  'custom-path': 'rect',
});

/** Mask shape → the editor frame shape that crops the image. */
export const FRAME_SHAPE_MAP = Object.freeze({
  rect: 'rectangle',
  'rounded-rect': 'rectangle',
  square: 'rectangle',
  circle: 'circle',
  ellipse: 'circle',
  triangle: 'triangle',
  polygon: 'polygon',
  star: 'star',
  blob: 'circle',
});

/** The editor element type each Design JSON V2 primitive becomes. */
export const ELEMENT_TYPES = Object.freeze({
  text: 'text', shape: 'shape', image: 'image', icon: 'shape', vector: 'shape', line: 'shape',
  frame: 'frame', group: 'group',
});

/**
 * The icon vocabulary the engine understands.
 *
 * These were emoji until the first real generations showed what that looks
 * like in a finished piece: a premium dental flyer carrying ✔️ ❤️ 👤 🔷.
 * Emoji are not iconography — they carry another vendor's illustration style
 * into someone else's brand, and they never read as premium.
 *
 * The editor has no icon or vector primitive, so an icon renders as a real
 * editor SHAPE. Three names have an exact geometric equivalent; the rest
 * render as a deliberate accent disc with the requested name preserved in
 * meta, ready for the day the editor gains real icon assets.
 */
export const ICON_NAMES = Object.freeze([
  'check', 'star', 'heart', 'arrow', 'phone', 'mail', 'location', 'globe',
  'clock', 'calendar', 'tag', 'cart', 'user', 'home', 'camera', 'play',
  'quote', 'logo-placeholder',
]);

/** Names with an exact shape in the editor's own vocabulary. */
export const ICON_SHAPES = Object.freeze({
  star: 'star', heart: 'heart', arrow: 'arrow', check: 'cross',
});

export const ICON_FALLBACK_SHAPE = 'circle';

/** Effects that become native editor shadow properties. */
export const SHADOW_DEFAULTS = Object.freeze({ x: 0, y: 8, blur: 24, color: 'rgba(0,0,0,0.18)', spread: 0 });
export const GLOW_DEFAULTS = Object.freeze({ blur: 24, color: 'rgba(255,255,255,0.6)' });

/** Glass: a translucent surface with a backdrop blur behind it. */
export const GLASS_DEFAULTS = Object.freeze({ blur: 18, opacity: 0.16, tint: '#ffffff' });

/** Where the engine records everything the editor has no native slot for. */
export const META_NAMESPACE = 'designV2';
